/**
 * Kind picker card — step 1 only (no NavStepper). Peer PaymentMethodOptionCard.
 */
import type { ReactNode } from 'react';
import type { MapViewKind } from '../shopMapViewModel.js';

function CheckmarkIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" aria-hidden="true">
      <path
        d="M3.5 8.2 6.4 11 12.5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export interface MapViewKindOptionCardProps {
  readonly kind: MapViewKind;
  readonly selected: boolean;
  readonly disabled?: boolean;
  readonly onSelect: (kind: MapViewKind) => void;
  readonly title: string;
  readonly description: string;
  readonly icon?: ReactNode;
  readonly testId: string;
}

export function MapViewKindOptionCard({
  kind,
  selected,
  disabled = false,
  onSelect,
  title,
  description,
  icon,
  testId,
}: MapViewKindOptionCardProps): JSX.Element {
  return (
    <label
      data-testid={testId}
      className={[
        'flex cursor-pointer items-start gap-3 rounded-2xl border-2 bg-[var(--color-surface)] px-4 py-3.5 transition-colors',
        selected ? 'border-[var(--color-accent)]' : 'border-[var(--color-border)]',
        disabled
          ? 'cursor-not-allowed opacity-60'
          : 'hover:border-[var(--color-accent)]/45',
      ].join(' ')}
    >
      <input
        type="radio"
        name="map-view-kind"
        value={kind}
        checked={selected}
        disabled={disabled}
        onChange={() => {
          onSelect(kind);
        }}
        className="sr-only"
      />
      {icon !== undefined ? (
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[var(--color-surface-muted)]">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1">
        <span className="block text-base font-semibold text-[var(--color-on-surface)]">
          {title}
        </span>
        <span className="mt-0.5 block text-sm text-[var(--color-on-surface-muted)]">
          {description}
        </span>
      </span>
      <span
        aria-hidden="true"
        className={[
          'mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2',
          selected
            ? 'border-[var(--color-accent)] bg-[var(--color-accent)] text-[var(--color-accent-foreground,#fff)]'
            : 'border-[var(--color-border)] bg-transparent',
        ].join(' ')}
      >
        {selected ? <CheckmarkIcon /> : null}
      </span>
    </label>
  );
}
